import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/shared/hooks/use-toast';
import { useI18n } from './I18nContext';
import { getBasename, getAuthCallbackUrl } from '@/utils/url';
import {
  identify as posthogIdentify,
  isPostHogEnabled,
  reset as posthogReset,
  setPersonProperties as posthogSetPersonProperties,
} from '@/integrations/analytics/posthog';
import { buildIdentifyTraits } from '@/integrations/analytics/identity';
import { registerSessionContext } from '@/integrations/analytics/sessionContext';
import { profileRepository } from '@/features/profile/repositories/supabaseProfileRepository';
import { classifySignUpError, logSignUpError } from '@/utils/authErrors';
import { getAttribution } from '@/utils/attribution';

// Custom hook for navigation
const useNavigation = () => {
  const navigate = (path: string) => {
    window.location.href = path;
  };
  return navigate;
};

/**
 * Sign-up metadata forwarded to GoTrue (`options.data`) and to the
 * `create_profile` RPC. Declared as a type alias, not an interface, so it keeps
 * the implicit index signature the RPC's `Json` parameter requires.
 */
export type SignUpUserData = {
  username: string;
  isInstructor: boolean;
  preferred_language: string;
  timezone: string;
  beltRank: string;
  birthday: string;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  // `error` is unknown by design: callers either check it for null or hand it to
  // classifySignUpError, which owns the narrowing.
  signUp: (email: string, password: string, userData: SignUpUserData) => Promise<{ error: unknown }>;
  signIn: (email: string, password: string) => Promise<{ error: unknown }>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Uid already identified to analytics, so overlapping auth events
  // (INITIAL_SESSION + SIGNED_IN) fetch the profile name only once.
  const analyticsUidRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useI18n();

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      // INITIAL_SESSION covers a restored session on page load, so users who
      // stay logged in still get their person properties. `posthogIdentify`
      // dedupes repeat calls for the same uid.
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
        identifyAnalyticsUser(session.user);
      }

      // Update timezone retroactively when user signs in
      if (event === 'SIGNED_IN' && session?.user) {
        updateTimezoneIfMissing(session.user.id);
        // Save OneSignal player_id if available
        saveOneSignalPlayerIdOnLogin(session.user.id);
      }

      if (event === 'SIGNED_OUT') {
        analyticsUidRef.current = null;
        posthogReset();
        registerSessionContext();
      }

      if (event === 'PASSWORD_RECOVERY') {
        navigate('/reset-password');
      }
    });

    // Initial session check
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (!session) {
          setUser(null);
          setSession(null);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session.user);
        setLoading(false);
      } catch (error) {
        if (mounted) {
          setUser(null);
          setSession(null);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Stitch the anonymous PostHog id to the Supabase auth uid (matches
  // profiles.id) with email and name as person properties. The profile's
  // full_name is the better name source, so it is $set once the row loads.
  const identifyAnalyticsUser = async (authUser: User) => {
    // No PostHog key → skip the profile lookup too, not just the SDK calls.
    if (!isPostHogEnabled()) return;
    if (analyticsUidRef.current === authUser.id) return;
    analyticsUidRef.current = authUser.id;
    posthogIdentify(authUser.id, buildIdentifyTraits(authUser));
    try {
      const fullName = await profileRepository.getFullName(authUser.id);
      // A sign-out (or another user's sign-in) during the lookup must not
      // write this user's name onto whoever is identified now.
      if (analyticsUidRef.current !== authUser.id) return;
      const traits = buildIdentifyTraits(authUser, fullName);
      if (traits.name) posthogSetPersonProperties({ name: traits.name });
    } catch {
      /* never throw from analytics */
    }
  };

  // Save OneSignal player_id on login if available
  const saveOneSignalPlayerIdOnLogin = async (userId: string) => {
    try {
      // Import OneSignalService
      const { default: oneSignalService } = await import('@/services/OneSignalService');
      
      // 1. First, check if player_id exists in database
      const { data: existingPlayers, error: dbError } = await supabase
        .from('onesignal_players')
        .select('player_id')
        .eq('user_id', userId)
        .eq('platform', 'web')
        .limit(1);
      
      if (existingPlayers && existingPlayers.length > 0 && existingPlayers[0].player_id) {
        return;
      }
      
      let retryCount = 0;
      const maxRetries = 20;
      
      while (!oneSignalService.getSubscriptionState().isInitialized && retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 500));
        retryCount++;
      }
      
      if (!oneSignalService.getSubscriptionState().isInitialized) {
        oneSignalService.setUserId(userId);
        return;
      }
      
      oneSignalService.setUserId(userId);
      
      const currentState = oneSignalService.getSubscriptionState();
      const existingPlayerId = currentState.playerId;
      const isOptedIn = currentState.isSubscribed;
      
      if (existingPlayerId && isOptedIn) {
        const { error } = await supabase
          .from('onesignal_players')
          .upsert({
            user_id: userId,
            player_id: existingPlayerId,
            platform: 'web',
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,player_id'
          });
        
        if (error) {
          console.error('❌ [AuthContext] Error saving existing player_id:', error);
        }
        return;
      }
      
      if (Notification.permission !== 'granted') {
        return;
      }
      
      const pushGranted = localStorage.getItem('push_notification_granted');
      if (pushGranted !== '1') {
        return;
      }
      
      const subscribeSuccess = await oneSignalService.subscribe();
      
      if (subscribeSuccess) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const newState = oneSignalService.getSubscriptionState();
        const newPlayerId = newState.playerId;
        
        if (newPlayerId) {
          const { error } = await supabase
            .from('onesignal_players')
            .upsert({
              user_id: userId,
              player_id: newPlayerId,
              platform: 'web',
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'user_id,player_id'
            });
          
          if (error) {
            console.error('❌ [AuthContext] Error saving new player_id:', error);
          }
        }
      }
    } catch (error) {
      console.error('❌ [AuthContext] Error in saveOneSignalPlayerIdOnLogin:', error);
    }
  };

  // Update timezone retroactively for existing users
  const updateTimezoneIfMissing = async (userId: string) => {
    try {
      // Get current timezone from browser
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Check if profile already has timezone
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('timezone')
        .eq('id', userId)
        .single();

      if (fetchError) {
        console.warn('[AuthContext] Error fetching profile for timezone update:', fetchError);
        return;
      }

      // If timezone is missing, update it
      if (!profile?.timezone) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ timezone: browserTimezone })
          .eq('id', userId);

        if (updateError) {
          // Silent fail - don't block user experience
        }
      }
    } catch (error) {
      console.warn('[AuthContext] Error in updateTimezoneIfMissing:', error);
      // Silent fail - don't block user experience
    }
  };

  const signUp = async (email: string, password: string, userData: SignUpUserData) => {
    try {
      const normalizedEmail = email.trim();
      const redirectUrl = getAuthCallbackUrl();

      // Where this person came from, captured at landing. Enriched once and
      // used for both writes below: GoTrue stores it in raw_user_meta_data, and
      // create_profile is what actually persists it on the profile row.
      const signUpMetadata = { ...userData, ...getAttribution() };

      // First, create the user in authentication
      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: signUpMetadata
        }
      });
    
      if (error) {
        logSignUpError(error, classifySignUpError(error).reason);
        // Error presentation belongs to AuthForm, which classifies the returned
        // error with classifySignUpError and shows a single translated message.
        // Only the "already registered" redirect stays here (it changes routing,
        // not messaging).
        if (error.message.includes('already registered') || error.message.includes('violates foreign key constraint')) {
          toast({
            title: t('auth.email_already_registered'),
            description: t('auth.email_already_registered_description'),
            variant: "destructive"
          });
          // Redirect to sign in page
          navigate('/');
        }
        // Every other failure — weak password, email send, database — is presented
        // by AuthForm from the error returned below. Nothing to do here.
        return { error };
      }

      if (!data.user) {
        console.error('No user data returned');
        toast({
          title: t('auth.sign_up_error'),
          description: t('auth.no_user_data_returned'),
          variant: "destructive"
        });
        return { error: new Error(t('auth.no_user_data_returned')) };
      }

      // Wait for the user to be created in the database
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Now create the profile
      const { error: profileError } = await supabase.rpc('create_profile', {
        user_id: data.user.id,
        user_metadata: signUpMetadata
      });

      if (profileError) {
        console.error('Error creating profile:', profileError);
        // Show friendly error message instead of raw database error
        toast({
          title: t('auth.sign_up_error'),
          description: t('auth.failed_to_create_account'),
          variant: "destructive"
        });
        // Return the original error so the UI can inspect code/details
        return { error: profileError };
      }

      return { error: null };
    } catch (error) {
      console.error('Unexpected error during signup:', error);
      toast({
        title: t('auth.error'),
        description: t('auth.unexpected_error'),
        variant: "destructive"
      });
    return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
    
      if (error) {
        console.log('Supabase sign in error:', error);
        
        // Handle specific error cases
        if (error.message?.toLowerCase().includes('email not confirmed') || 
            error.code === 'email_not_confirmed') {
          toast({
            title: t('auth.email_not_confirmed'),
            description: t('auth.email_not_confirmed_description'),
            variant: "destructive"
          });
        } else if (error.message?.toLowerCase().includes('invalid login credentials') ||
                   error.message?.toLowerCase().includes('invalid email or password')) {
          toast({
            title: t('auth.sign_in_failed'),
            description: t('auth.sign_in_failed_description'),
            variant: "destructive"
          });
        } else if (error.message?.toLowerCase().includes('too many requests')) {
          toast({
            title: t('auth.error'),
            description: t('auth.too_many_requests'),
            variant: "destructive"
          });
        } else {
          // For any other specific errors, show a generic but informative message
          toast({
            title: t('auth.sign_in_failed'),
            description: error.message || t('auth.sign_in_failed_description'),
            variant: "destructive"
          });
        }
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Unexpected error during signin:', error);
      toast({
        title: t('auth.error'),
        description: t('auth.unexpected_error'),
        variant: "destructive"
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Clear local state first
      setUser(null);
      setSession(null);
      
      // Clear wizard session storage
      sessionStorage.removeItem('showWizard');
      sessionStorage.removeItem('wizardShownThisSession');
      
      // Clear all Supabase session data from localStorage
      localStorage.removeItem('sb-kskwramebgqbbmahvloe-auth-token');
      
      // Then try to sign out from Supabase
      await supabase.auth.signOut();
      
      // Force reload the page to clear any remaining state
      window.location.href = getBasename();
    } catch (error) {
      console.error('Error in signOut:', error);
      // Even if there's an error, force reload to clear everything
      window.location.href = getBasename();
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

